/**
 * Maps API responses from index-aligned endpoints to new_index component props.
 */

function stripHtmlBreaks(text) {
  if (!text) return '';
  return String(text).replace(/#/g, '').replace(/<br\s*\/?>/gi, ' ').trim();
}

function decodeHtmlEntities(text) {
  if (!text) return '';
  return String(text)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
}

function normalizePlainText(text) {
  if (!text) return '';
  return decodeHtmlEntities(stripHtmlTags(stripHtmlBreaks(text)))
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripHtmlTags(text) {
  if (!text) return '';
  return String(text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function mapInternationalSummaryHtml(item) {
  const raw =
    item?.desc ||
    item?.summary ||
    item?.intro ||
    item?.description ||
    item?.content ||
    '';
  if (!raw) return '';

  const decoded = decodeHtmlEntities(String(raw)).replace(/#/g, '');
  if (/<[a-z][\s\S]*>/i.test(decoded)) {
    return decoded.trim();
  }

  return decoded
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('<br>')
    .trim();
}

export function mapIndexNewsItem(item) {
  return {
    id: item.id,
    title: item.title || '',
    imageUrl: item.thumb || '',
    desc: item.desc || item.summary || '',
    category: item.category || item.cate_name || '',
    date: item.created_at || item.date || '',
  };
}

export function mapIndexNewsList(list) {
  const items = Array.isArray(list) ? list : list?.data ?? [];
  if (!Array.isArray(items)) return [];
  return items.map(mapIndexNewsItem);
}

export function mapIndexSliderItem(item, index) {
  return {
    id: item.id ?? `slider-${index}`,
    imageUrl: item.thumb || '',
    subSlogan: item.title || item.desc || '',
    url: item.url || '',
  };
}

export function mapIndexSliderList(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapIndexSliderItem);
}

export function mapTaleListItem(item) {
  return {
    id: item.id,
    title: stripHtmlBreaks(item.title),
    imageUrl: item.thumb || '',
    href: `/tale_detail?id=${item.id}`,
    date: item.created_at || item.date || '',
  };
}

export function mapTaleList(list, limit = 4) {
  if (!Array.isArray(list)) return [];
  return list.slice(0, limit).map(mapTaleListItem);
}

function isExternalLink(url) {
  return /^https?:\/\//i.test(String(url || '').trim());
}

function unwrapShanhaiQingdaoList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.data)) return res.data;
  if (res.data && typeof res.data === 'object' && res.data.id != null) return [res.data];
  if (res.id != null) return [res];
  return [];
}

export function mapShanhaiQingdaoItem(item) {
  const linkUrl = String(item?.link_url || '').trim();
  const external = isExternalLink(linkUrl);
  return {
    id: item.id,
    title: stripHtmlBreaks(item.title),
    imageUrl: item.thumb || '',
    href: external ? linkUrl : `/shanhai-qingdao/${item.id}`,
    external,
    content: item.content || '',
    listorder: item.listorder ?? 0,
    display: item.display ?? 1,
  };
}

export function mapShanhaiQingdaoList(res) {
  return unwrapShanhaiQingdaoList(res)
    .filter((item) => Number(item.display) !== 0)
    .sort((a, b) => (a.listorder ?? 0) - (b.listorder ?? 0))
    .map(mapShanhaiQingdaoItem);
}

export function mapShanhaiQingdaoDetail(res) {
  const payload = res && typeof res === 'object' && res.data !== undefined && res.id === undefined
    ? res.data
    : res;
  if (!payload || payload.id == null) return null;
  const mapped = mapShanhaiQingdaoItem(payload);
  return {
    title: mapped.title,
    content: mapped.content,
    thumb: mapped.imageUrl,
  };
}

function isInternationalVideoItem(item) {
  const typeText = item?.type_text || '';
  return typeText === '视频' || Number(item?.type) === 2 || !!item?.video;
}

function unwrapInternationalIndexPayload(data) {
  if (!data || typeof data !== 'object') {
    return { cates: [], items: [] };
  }
  if (Array.isArray(data.cates) || Array.isArray(data.items)) {
    return data;
  }
  if (data.data && typeof data.data === 'object') {
    return unwrapInternationalIndexPayload(data.data);
  }
  return data;
}

export function mapInternationalCate(cate) {
  const id = cate?.id;
  return {
    id,
    title: cate?.title || '',
    thumb: cate?.thumb || '',
    imageUrl: cate?.thumb || '',
    href: id != null ? `/international?cate_id=${id}` : '/international',
  };
}

export function mapInternationalCateList(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapInternationalCate);
}

export function mapInternationalItem(item) {
  const id = item?.id;
  const isVideo = isInternationalVideoItem(item);
  return {
    id,
    title: normalizePlainText(item?.title),
    thumb: item?.thumb || '',
    imageUrl: item?.thumb || '',
    typeText: item?.type_text || (isVideo ? '视频' : '图文'),
    tag: item?.type_text || (isVideo ? '视频' : '图文'),
    summary: mapInternationalSummaryHtml(item),
    video: item?.video || '',
    cate: item?.cate || null,
    cateId: item?.cate?.id ?? item?.cate_id ?? null,
    href: isVideo
      ? `/international_video?id=${id}`
      : `/international_detail?id=${id}`,
    isVideo,
    direction: item?.direction ?? null,
    isPortrait: Number(item?.direction) === 3,
  };
}

export function mapInternationalItemList(list, limit) {
  if (!Array.isArray(list)) return [];
  const items = limit ? list.slice(0, limit) : list;
  return items.map(mapInternationalItem);
}

export function mapInternationalIndex(data) {
  const payload = unwrapInternationalIndexPayload(data);
  const cates = payload?.cates ?? [];
  const items = payload?.items ?? [];
  return {
    cates: mapInternationalCateList(cates),
    items: mapInternationalItemList(items),
  };
}

export function mapAskQingdaoItem(item) {
  const lines = [item.title_line1, item.title_line2].filter(
    (line) => line != null && String(line).trim() !== ''
  );
  return {
    id: item.id,
    sort: item.sort ?? 0,
    iconUrl: item.icon || '',
    lines,
    redirectUrl: item.link_url || '#',
    isTitleOnly: lines.length <= 1,
  };
}

function unwrapAskQingdaoPayload(res) {
  if (!res || typeof res !== 'object') {
    return { section: null, items: [] };
  }
  if (res.section || res.items) {
    return res;
  }
  if (res.data && typeof res.data === 'object') {
    return res.data;
  }
  return { section: null, items: [] };
}

export function mapAskQingdaoIndex(res) {
  const payload = unwrapAskQingdaoPayload(res);
  const rawItems = Array.isArray(payload.items) ? payload.items : [];
  const enabled = rawItems.filter((item) => Number(item.status) !== 0);
  const sorted = [...enabled].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

  return {
    section: {
      title: payload.section?.title || '',
      subtitle: payload.section?.subtitle || '',
    },
    items: sorted.map(mapAskQingdaoItem),
  };
}

export function mapReleaseListItem(item) {
  const id = item.id;
  const isVideo = Number(item.type) === 2;
  return {
    id,
    title: stripHtmlBreaks(item.title),
    imageUrl: item.thumb || '',
    date: item.created_at || item.date || '',
    href: isVideo ? `/publish_video?id=${id}` : `/publish_detail?id=${id}`,
  };
}

export function mapReleaseList(list, limit = 4) {
  const items = Array.isArray(list) ? list : list?.data ?? [];
  if (!Array.isArray(items)) return [];
  return items.slice(0, limit).map(mapReleaseListItem);
}

export function mapHotPicItem(item) {
  return {
    id: item.id,
    title: item.title || '',
    imageUrl: item.preview || item.thumb || '',
    detailUrl: `/topic_pic_detail?id=${item.id}`,
    category: '推荐',
  };
}

export function mapHotPicList(hot1 = [], hot2 = []) {
  const merged = [...(hot1 || []), ...(hot2 || [])];
  return merged.map(mapHotPicItem);
}

export function mapSubjectCatList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => ({
    id: item.id,
    title: item.title || '',
    thumb: item.thumb || '',
  }));
}

export function mapSubjectListItem(item) {
  return {
    id: item.id,
    title: item.title || '',
    imageUrl: item.thumb || '',
    detailUrl: `/special_topics?catid=${item.id}`,
    category: item.cate_id || '',
  };
}

export function mapSubjectListToPhotos(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapSubjectListItem);
}

export function mapVideoItem(item) {
  return {
    id: item.id,
    title: item.title || '',
    imageUrl: item.thumb || '',
    videoUrl: item.url || '',
    direction: item?.direction ?? null,
    isPortrait: Number(item?.direction) === 3,
  };
}

export function mapVideoList(res, limit = 5) {
  const list = res?.data ?? res;
  if (!Array.isArray(list)) return [];
  return list.slice(0, limit).map(mapVideoItem);
}

export function mapVrItem(item) {
  return {
    id: item.id,
    title: item.title || '',
    imageUrl: item.thumb || '',
    href: item.url || '',
  };
}

export function mapVrList(res, limit = 6) {
  const list = res?.list ?? res;
  if (!Array.isArray(list)) return [];
  return list.slice(0, limit).map(mapVrItem);
}

export function mapCuratedArticle(data, type = 'daily-scene') {
  const payload = data && typeof data === 'object' && data.data !== undefined && data.id === undefined
    ? data.data
    : data;

  if (!payload || payload === null || !payload.id) {
    return null;
  }

  const detailUrl = type === 'weekly-pick' ? '/weekly-pick' : '/daily-scene';

  return {
    id: payload.id,
    title: payload.title || '',
    thumb: payload.thumb || '',
    description: payload.description || '',
    content: payload.content || '',
    detailUrl,
    type,
  };
}
